// Position tab — numeric X/Y/W/H in INCHES + rotation (poster designers think
// in physical dimensions), page alignment, align-to-selection + distribute,
// and layer order. Absorbs the old align popover.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelectionProps } from '../hooks/useSelectionProps';
import { TbButton, TbNumberField } from '../primitives/toolbarPrimitives';
import {
  IconAlignLeft, IconAlignCenterH, IconAlignRight,
  IconAlignTop, IconAlignMiddleV, IconAlignBottom,
  IconLayerUp, IconLayerDown, IconLayerTop, IconLayerBottom,
} from '../toolbarIcons';

function Section({ title, children }) {
  return (
    <div className="cce-inspector-section">
      <h4>{title}</h4>
      {children}
    </div>
  );
}

export default function PositionTab({ editor, selectedObjects }) {
  const { t } = useTranslation('editor');
  const { objProps, apply } = useSelectionProps(editor, selectedObjects);
  const [lockAspect, setLockAspect] = useState(true);
  const isMulti = selectedObjects.length > 1;
  const pos = objProps.position;

  const setPos = (patch) =>
    apply(() => editor?.setPositionInches({ ...patch, lockAspect }), 'soon');

  return (
    <div className="cce-inspector-content">
      {!isMulti && pos && (
        <Section title={t('position.sizePosition')}>
          <div className="cce-inspector-grid2">
            <label>
              <span>{t('position.w')}</span>
              <TbNumberField label={t('position.widthInches')} value={Number(pos.w.toFixed(2))} min={0.1} max={500} step={0.25}
                onChange={(v) => setPos({ w: v })} />
            </label>
            <label>
              <span>{t('position.h')}</span>
              <TbNumberField label={t('position.heightInches')} value={Number(pos.h.toFixed(2))} min={0.1} max={500} step={0.25}
                onChange={(v) => setPos({ h: v })} />
            </label>
            <label>
              <span>{t('position.x')}</span>
              <TbNumberField label={t('position.xInches')} value={Number(pos.x.toFixed(2))} min={-500} max={500} step={0.25}
                onChange={(v) => setPos({ x: v })} />
            </label>
            <label>
              <span>{t('position.y')}</span>
              <TbNumberField label={t('position.yInches')} value={Number(pos.y.toFixed(2))} min={-500} max={500} step={0.25}
                onChange={(v) => setPos({ y: v })} />
            </label>
            <label>
              <span>{t('position.angle')}</span>
              <TbNumberField label={t('position.rotationDegrees')} value={pos.angle} min={-360} max={360} step={1}
                onChange={(v) => setPos({ angle: v })} />
            </label>
            <label className="cce-inspector-checkbox">
              <input type="checkbox" checked={lockAspect} onChange={(e) => setLockAspect(e.target.checked)} />
              <span>{t('position.lockRatio')}</span>
            </label>
          </div>
        </Section>
      )}

      <Section title={t('position.alignToPage')}>
        <div className="cce-tb-align-grid">
          <TbButton icon={<IconAlignLeft />} label={t('position.alignLeft')} onClick={() => apply(() => editor?.alignLeft())} />
          <TbButton icon={<IconAlignCenterH />} label={t('position.centerH')} onClick={() => apply(() => editor?.alignCenterH())} />
          <TbButton icon={<IconAlignRight />} label={t('position.alignRight')} onClick={() => apply(() => editor?.alignRight())} />
          <TbButton icon={<IconAlignTop />} label={t('position.alignTop')} onClick={() => apply(() => editor?.alignTop())} />
          <TbButton icon={<IconAlignMiddleV />} label={t('position.centerV')} onClick={() => apply(() => editor?.alignMiddleV())} />
          <TbButton icon={<IconAlignBottom />} label={t('position.alignBottom')} onClick={() => apply(() => editor?.alignBottom())} />
        </div>
      </Section>

      {isMulti && (
        <>
          <Section title={t('position.alignToEachOther')}>
            <div className="cce-tb-align-grid">
              <TbButton icon={<IconAlignLeft />} label={t('position.selAlignLeft')} onClick={() => apply(() => editor?.alignSelection('left'))} />
              <TbButton icon={<IconAlignCenterH />} label={t('position.selCenterH')} onClick={() => apply(() => editor?.alignSelection('centerH'))} />
              <TbButton icon={<IconAlignRight />} label={t('position.selAlignRight')} onClick={() => apply(() => editor?.alignSelection('right'))} />
              <TbButton icon={<IconAlignTop />} label={t('position.selAlignTop')} onClick={() => apply(() => editor?.alignSelection('top'))} />
              <TbButton icon={<IconAlignMiddleV />} label={t('position.selCenterV')} onClick={() => apply(() => editor?.alignSelection('middleV'))} />
              <TbButton icon={<IconAlignBottom />} label={t('position.selAlignBottom')} onClick={() => apply(() => editor?.alignSelection('bottom'))} />
            </div>
          </Section>
          <Section title={t('position.distribute')}>
            <div className="cce-inspector-row">
              <button type="button" className="cce-inspector-mini-btn" onClick={() => apply(() => editor?.distribute('h'))}>
                {t('position.spaceEvenlyH')}
              </button>
              <button type="button" className="cce-inspector-mini-btn" onClick={() => apply(() => editor?.distribute('v'))}>
                {t('position.spaceEvenlyV')}
              </button>
            </div>
          </Section>
        </>
      )}

      <Section title={t('position.layerOrder')}>
        <div className="cce-inspector-row">
          <TbButton icon={<IconLayerUp />} label={t('position.bringForward')} onClick={() => apply(() => editor?.bringForward())} />
          <TbButton icon={<IconLayerDown />} label={t('position.sendBackward')} onClick={() => apply(() => editor?.sendBackwards())} />
          <TbButton icon={<IconLayerTop />} label={t('position.bringToFront')} onClick={() => apply(() => editor?.moveLayerToTop(selectedObjects[0]))} />
          <TbButton icon={<IconLayerBottom />} label={t('position.sendToBack')} onClick={() => apply(() => editor?.moveLayerToBottom(selectedObjects[0]))} />
        </div>
      </Section>
    </div>
  );
}
